<template>
	<div
		ref="cardRoot"
		:class="cn('relative', props.rootClass)"
		data-test-id="property-card"
		v-bind="attrsWithoutDataTest"
	>
		<component :is="wrapperComponent" v-bind="wrapperProps">
			<Card
				:class="
					cn(
						propertyCardBodyVariants({ size, variant }),
						props.class,
						'cursor-pointer min-h-fit',
						isShortsSrpImageCard && 'sm:h-full lg:h-[378px]'
					)
				"
				@click="handlePropertyClicked"
			>
				<PropertyCardHeader
					v-bind="forwardedProps"
					:is-mouse-over-card-header="isMouseOverCardHeader"
					:card-position="cardPosition"
					:section="section"
					:size="headerSize"
					:hide-favorite-button="true"
					:is-property-active="isPropertyActive"
					class="group/card z-10"
					@mouseover="handleMouseOver"
					@mouseleave="handleMouseLeave"
				>
					<PropertyCardStatusBadges
						:property="property"
						:is-property-active="isPropertyActive"
					/>
					<PropertyCardContent
						v-if="isPropertyActive"
						v-bind="forwardedProps"
						@mouseover="handleMouseOver"
						@mouseleave="handleMouseLeave"
					/>

					<PropertyCardFooter
						v-if="isFullFace"
						v-bind="forwardedProps"
						:as-tags="asTags"
						:class="props.footerClass"
					/>
				</PropertyCardHeader>

				<PropertyCardFooter
					v-if="!isFullFace"
					v-bind="forwardedProps"
					:size="footerSize"
					:class="props.footerClass"
				/>
			</Card>
		</component>

		<!-- This button needs to be outside the NuxtLink because the link opens a new tab when clicked-->
		<!-- ClientOnly ensures favorite state is only rendered on client to improve cache hit ratio -->
		<!-- SSR: Shows inactive star, Client: Updates to real state -->
		<!-- v-if must be outside ClientOnly to ensure matching DOM structure during hydration -->
		<div
			v-if="isValidData"
			:class="cn('absolute right-4 top-4 z-40', props.favoriteButtonClass)"
		>
			<ClientOnly>
				<template #fallback>
					<FavoriteButton
						:is-favorite="false"
						:property-id="property.id"
						:commercial-id="property.commercialId"
					/>
				</template>
				<FavoriteButton
					:key="`favorite-${property.id}-${isUserLoggedIn}`"
					v-model:is-favorite="isFavorite"
					:property-id="property.id"
					:commercial-id="property.commercialId"
					:disable-interaction="props.isDragging || isListScrolling"
					@toggle="toggleFavorite"
					@error="handleWishlistError"
				/>
			</ClientOnly>
		</div>
	</div>
</template>

<script lang="ts" setup>
import type { PropertyCardEmits, PropertyCardProps } from '.';
import type { ImageClickedEvent } from '../Card/CardCarousel';
import type { SectionName } from '~/types/mixpanel';

import { cn, hasShortsVideo } from '@/utils';
import { NuxtLink } from '#components';
import { useEvents } from '~/composables/events/useEvents';
import { useImpressionBatch } from '~/composables/useImpressionBatch';
import { useListingEventTracking } from '~/composables/useListingEventTracking';
import { useToggleFavoriteSync } from '~/composables/useToggleFavoriteSync';
import { MixpanelProps } from '~/core/service/mixpanel';
import { isNumber } from '~~/utils';
import { useForwardProps } from 'reka-ui';
import { computed, ref } from 'vue';

import { useCarouselScrolling } from '../Card/CardCarousel/useCarouselScrolling';
import { FavoriteButton } from '../FavoriteButton';
import { Card } from '../ui/card';
import {
	propertyCardBodyVariants,
	PropertyCardContent,
	PropertyCardFooter,
	PropertyCardHeader
} from './index';
import PropertyCardStatusBadges from './PropertyCardStatusBadges.vue';

defineOptions({
	inheritAttrs: false
});

const attrs = useAttrs();

const attrsWithoutDataTest = computed(() => {
	const { 'data-test': _, ...rest } = attrs;
	return rest;
});

const props = withDefaults(defineProps<PropertyCardProps>(), {
	variant: 'whiteBackground',
	size: 'sm',
	disableLink: false,
	showActions: false
});

const emit = defineEmits<PropertyCardEmits>();

const isFullFace = computed(() => props.variant === 'fullFace');
const isShortsSrpImageCard = computed(
	() => props.headerSize === 'shortsSrpMobile' && !isFullFace.value
);

const propertyDetailsPath = computed(() => {
	const { commercialId, slug } = props.property ?? {};
	if (!commercialId) return null;

	return slug ? `/imovel/${commercialId}/${slug}` : `/imovel/${commercialId}`;
});

const isLinkEnabled = computed(() => {
	return !props.disableLink && !!propertyDetailsPath.value;
});

// Dynamic wrapper component: NuxtLink when enabled, div when disabled
const wrapperComponent = computed(() =>
	isLinkEnabled.value ? NuxtLink : 'div'
);

// Props for the wrapper component
const wrapperProps = computed(() => {
	const responsiveClass = isShortsSrpImageCard.value
		? 'sm:block sm:h-full lg:h-auto'
		: undefined;

	if (!isLinkEnabled.value) {
		return { class: responsiveClass };
	}
	return {
		to: propertyDetailsPath.value,
		target: '_blank',
		rel: 'noopener noreferrer',
		class: responsiveClass
	};
});

const route = useRoute();
const { trackPropertyCardClicked } = useEvents();
const { trackClick } = useListingEventTracking();
const { loggedIn: isUserLoggedIn } = useUserSession();

const isMouseOverCardHeader = ref(false);

const eventData = {
	currentPage: route.fullPath,
	tagExclusive: props.property.isExclusive,
	companyName: props.property.company?.name,
	agentName: props.property.agent?.name,
	clickCard: true,
	videoShorts: hasShortsVideo(props.property)
};

// Pass the real values to the composable
const propertyId = computed(() => props.property.id);
const commercialId = computed(() => props.property.commercialId);
const initialFavoriteState = computed(
	() => props.property?.inUserWishlist ?? false
);

// useToggleFavoriteSync is synchronous - no await needed
// This prevents blocking SSR rendering for each property card
const {
	isFavorite,
	handleToggleFavorite: toggleFavorite,
	isValidData
} = useToggleFavoriteSync(
	propertyId,
	commercialId,
	eventData,
	props.section,
	initialFavoriteState,
	{ property: () => props.property }
);

// Mesmo contexto (com debounce de ociosidade) que pausa os carrosseis durante
// o scroll do documento. Toque no favorito enquanto a lista ainda se move e o
// dedo freando o momentum, nao intencao de favoritar — so dispara em repouso.
// Fora de um provedor (ex.: paginas sem SRP) o default e `false` e nada muda.
const { isScrolling: isListScrolling } = useCarouselScrolling();

function handleMouseOver() {
	isMouseOverCardHeader.value = true;
}
function handleMouseLeave() {
	isMouseOverCardHeader.value = false;
}

const delegatedProps = computed(() => {
	const {
		class: _,
		section: _s,
		cardPosition: _c,
		asTags: _a,
		disableLink: _d,
		headerSize: _h,
		rootClass: _r,
		footerClass: _f,
		...delegated
	} = props;

	return delegated;
});

const forwardedProps = useForwardProps(delegatedProps);

const section = computed<SectionName | undefined>(() => {
	if (props.section) {
		return props.section;
	}

	if (route.fullPath.startsWith('/venda/mapa-de-imoveis')) {
		return 'listed properties';
	}

	return undefined;
});

const footerSize = computed(() => {
	if (props.showActions) {
		return 'lg';
	}
	return 'sm';
});

const headerSize = computed(() => props.headerSize ?? props.size);

const isPropertyActive = computed(
	() => props.property.publicationStatus === 'published'
);

const cardRoot = ref<HTMLElement | null>(null);

const shouldTrackImpressions = computed(
	() =>
		isLinkEnabled.value &&
		!!props.trackingContext &&
		isNumber(props.positionInList)
);

// Nulling the target unobserves the card and cancels any pending 500ms timer.
const impressionTarget = computed<HTMLElement | null>(() =>
	shouldTrackImpressions.value ? cardRoot.value : null
);

useImpressionBatch(impressionTarget, () => ({
	propertyId: props.property.id,
	trackingContext: props.trackingContext!,
	positionInList: props.positionInList!
}));

const trackingSharedProps = computed(() => ({
	[MixpanelProps.COMMERCIAL_ID]: props.property.commercialId,
	[MixpanelProps.CURRENT_PAGE]: route.fullPath,
	[MixpanelProps.SECTION_NAME]: section.value || '',
	[MixpanelProps.CARD_POSITION]: isNumber(props.cardPosition)
		? props.cardPosition
		: undefined,
	[MixpanelProps.CARD_TYPE]:
		props.variant === 'fullFace' ? 'full face' : 'regular card',
	[MixpanelProps.COMPANY_NAME]: props.property.company.name,
	[MixpanelProps.TAG_EXCLUSIVE]: props.property.isExclusive,
	[MixpanelProps.AGENT_NAME]: props.property.agent.name
}));

function handlePropertyClicked(event: ImageClickedEvent) {
	emit('card-click');

	const clickedAt = new Date().toISOString();

	trackPropertyCardClicked({
		[MixpanelProps.DESTINATION_URL]: propertyDetailsPath.value ?? '',
		[MixpanelProps.PHOTO_URL]: event.photoUrl,
		[MixpanelProps.PHOTO_POSITION]: event.photoPosition,
		...trackingSharedProps.value
	});

	emitListingClickEvent(clickedAt);
}

function emitListingClickEvent(clickedAt: string) {
	if (!isLinkEnabled.value) return;
	if (!props.trackingContext || !isNumber(props.positionInList)) return;

	const position = props.positionInList + 1;

	trackClick({
		propertyId: props.property.id,
		clickedAt,
		context: {
			...props.trackingContext,
			url: route.path,
			position
		}
	});
}

function handleWishlistError(error: string) {
	// To do on toast tasks
}
</script>
